import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";
import {
  fetchProjects, createProject, updateProject, deleteProject,
  fetchClients, createClient, updateClient, deleteClient,
  fetchEmployees, fetchTimeEntries, getWsUrl, getWsToken,
} from "../api";
import ConfirmDialog from "./ConfirmDialog";
import CustomSelect from "./CustomSelect";
import { MONTHS_SHORT, WEEKDAYS } from "../constants";
import { pad2, getMonday, addDays, getISOWeek, getMondayOfWeek, lastWeekOfYear, parseISODateLocal, useToday } from "../lib/dates";
import { fmtDurationSec } from "../lib/format";
import "./Time.css"; // re-use chart styles
import "./FilterBar.css"; // re-use filter-bar / filter-chip styles
import "./AreaView.css"; // re-use btn-filter-toggle / filter-overlay styles
import "./TaskForm.css"; // re-use cat-suggestions / task-form-label / task-cat-wrap
import "./WeekPicker.css"; // re-use week-picker-dropdown for the range quick-select
import "./ProjectsPage.css";

const CLIENT_PALETTE = [
  "#5ba3d9", "#8a6fd4", "#2bb3a3", "#c99f63", "#7da87d",
  "#d97e7e", "#5b8acf", "#a87dc4", "#67b1c4", "#caa45e",
];
function hashStr(s) { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); }
function defaultClientColor(name) {
  if (!name) return "#6b7280";
  return CLIENT_PALETTE[hashStr(name) % CLIENT_PALETTE.length];
}

// Auto-Kürzel fallback: first 3 letters of the name (umlauts included), uppercased.
// Used both for clients without a stored abbreviation and to prefill the form.
function autoClientCode(name) {
  return (name || "").replace(/[^A-Za-zÄÖÜäöüß]/g, "").slice(0, 3).toUpperCase();
}
// PR-A: planning starts in 2024 — the arrows and the quick-select share this floor.
const PLAN_START = new Date(2024, 0, 1);
const PLAN_START_YEAR = 2024;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const activeEmployees = useMemo(() => employees.filter((e) => !e.archived), [employees]);
  const [entries, setEntries] = useState([]);
  // First-paint gate — see Time.js: hold the view until the first load finishes.
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  const [editingProject, setEditingProject] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(null); // {name, count}
  const [showArchived, setShowArchived] = useState(false); // toggle: also show archived projects
  // Mobile-only filter sheet (same pattern as Kalender/Liste/Roadmap). On the
  // desktop the wrapper is display:none, so this state is never reachable there.
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef(null);
  const [focusedIds, setFocusedIds] = useState(() => new Set()); // project ids shown in the chart; empty = all
  const [weekOffset, setWeekOffset] = useState(0);
  // Clients are stored server-side (a real table), not in localStorage anymore.
  const [clients, setClients] = useState([]);
  const [collapsedClients, setCollapsedClients] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("projectCollapsedClients") || "[]")); } catch { return new Set(); }
  });
  const [editingClient, setEditingClient] = useState(null); // {id, name, color, code, isNew} | null
  const [infoProject, setInfoProject] = useState(null); // project whose person breakdown is shown

  const clientByName = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.name] = c;
    return m;
  }, [clients]);
  const colorForClient = useCallback(
    (name) => clientByName[name]?.color || defaultClientColor(name),
    [clientByName]
  );
  // Defined Kürzel, or an auto code (first 3 letters uppercase) as fallback.
  const codeForClient = useCallback(
    (name) => clientByName[name]?.abbreviation || autoClientCode(name),
    [clientByName]
  );

  const toggleClientCollapse = (name) => {
    setCollapsedClients((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem("projectCollapsedClients", JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  // Always fetch the FULL list (incl. archived): the Alle/Aktive toggle filters
  // client-side via `filtered`, and the Kürzel suggestion/duplicate checks need
  // archived projects too — otherwise a suggested code could silently collide
  // with an archived one. Bonus: stable deps, so the WebSocket effect no longer
  // reconnects on every toggle.
  const loadProjects = useCallback(async () => {
    try { setProjects(await fetchProjects(true)); } catch (e) { setErr(e.message); }
  }, []);
  const loadClients = useCallback(async () => {
    try { setClients(await fetchClients()); } catch (e) { /* ignore */ }
  }, []);
  const loadEmployees = useCallback(async () => {
    // Include archived so departed staff's tracked hours resolve to a name in the
    // hours overview; the project person-picker uses `activeEmployees` below.
    try { setEmployees(await fetchEmployees(true)); } catch (e) { /* ignore */ }
  }, []);
  const loadEntries = useCallback(async () => {
    try { setEntries(await fetchTimeEntries()); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([loadProjects(), loadClients(), loadEmployees(), loadEntries()]).finally(() => setReady(true));
  }, [loadProjects, loadClients, loadEmployees, loadEntries]);

  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(getWsUrl());
      ws.onopen = () => {
        const token = getWsToken();
        if (token) ws.send(JSON.stringify({ token }));
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "workforce") { loadProjects(); loadClients(); loadEntries(); }
          if (msg.type === "users") loadEmployees();
        } catch {}
      };
      // disposed guard: the cleanup's ws.close() fires onclose asynchronously —
      // without it every unmount/dep-change left a zombie socket reconnecting.
      ws.onclose = () => { if (!disposed) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };
    let disposed = false;
    connect();
    return () => { disposed = true; clearTimeout(reconnectTimer); if (ws) ws.close(); };
  }, [loadProjects, loadClients, loadEmployees, loadEntries]);

  // Close the filter sheet on an outside click (mirrors Roadmap/Calendar).
  useEffect(() => {
    if (!showFilters) return;
    const onDoc = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilters(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showFilters]);

  const handleSave = async (form, existing) => {
    setErr("");
    if (existing?.id) {
      await updateProject(existing.id, { ...form, version: existing.version });
    } else {
      await createProject(form);
    }
    await loadProjects();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try { await deleteProject(confirmDelete.id); }
    catch (e) { setErr(e.message); }
    setConfirmDelete(null);
    loadProjects();
  };

  const handleDeleteClient = async () => {
    if (!confirmDeleteClient) return;
    // The server deletes the client AND all of its projects in one call.
    try { await deleteClient(confirmDeleteClient.id); }
    catch (e) { setErr(e.message); }
    setConfirmDeleteClient(null);
    setEditingClient(null);
    await Promise.all([loadProjects(), loadClients()]);
  };

  // All client names that exist on any project (for the filter chips)
  // All known clients: those used on a project PLUS those defined standalone
  // (color/Kürzel stored) — so a client can exist before it has any project.
  const allClients = useMemo(() => {
    const set = new Set();
    for (const c of clients) if (c.name) set.add(c.name);
    // Defensive: a project referencing a client that isn't in the table yet.
    for (const p of projects) { const c = (p.client || "").trim(); if (c) set.add(c); }
    return [...set].sort((a, b) => a.localeCompare(b, "de"));
  }, [clients, projects]);

  // Filtered list: hide archived projects unless the toggle is on. Client selection
  // happens via the per-client chart-focus buttons, not a list filter.
  const filtered = useMemo(
    () => projects.filter((p) => showArchived || !p.archived),
    [projects, showArchived]
  );

  // group by client, then sort. Known clients with no (visible) project still get
  // an empty group so they show up and can receive a project via the "+" button.
  const grouped = useMemo(() => {
    const map = {};
    for (const c of allClients) map[c] = [];
    for (const p of filtered) {
      const key = (p.client || "").trim() || "__no_client__";
      (map[key] = map[key] || []).push(p);
    }
    for (const k of Object.keys(map)) {
      // Sort by Kürzel ascending (001 before 002 …); projects without one go last.
      map[k].sort((a, b) => {
        const aa = a.abbreviation || "";
        const bb = b.abbreviation || "";
        if (!aa && !bb) return a.name.localeCompare(b.name, "de");
        if (!aa) return 1;
        if (!bb) return -1;
        return aa.localeCompare(bb, "de", { numeric: true, sensitivity: "base" });
      });
    }
    return map;
  }, [filtered, allClients]);
  const clientKeys = useMemo(() => {
    let keys = Object.keys(grouped);
    // "Aktive": only show clients that actually have a visible (active) project.
    // "Alle": show every known client, even those without any project yet.
    if (!showArchived) keys = keys.filter((k) => (grouped[k] || []).length > 0);
    return keys.sort((a, b) => {
      if (a === "__no_client__") return 1;
      if (b === "__no_client__") return -1;
      return a.localeCompare(b, "de");
    });
  }, [grouped, showArchived]);

  // --- weekly chart per project ---
  // Reactive local date (rolls over after midnight / on tab focus) — a tab left
  // open overnight otherwise keeps yesterday's "Heute" marker and nav bounds.
  const todayISO = useToday();
  const today = useMemo(() => parseISODateLocal(todayISO), [todayISO]);
  const weekStart = useMemo(() => addDays(getMonday(today), weekOffset * 7), [today, weekOffset]);

  const [chartRange, setChartRange] = useState("year"); // "week"|"month"|"year"
  const [monthOffset, setMonthOffset] = useState(0);
  const [yearOffset, setYearOffset] = useState(0);

  const projectById = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);

  // The chart shows only the focused projects; when nothing is focused it shows
  // everything currently in the filtered list. Focus is a multi-select set —
  // toggled per project (row button) or per client (group-header button).
  // Focus pruned against the CURRENT project list: after deleting a focused
  // project its orphaned id used to keep the chart permanently empty with no
  // visible way to reset the focus.
  const allowedProjectIds = useMemo(() => {
    const live = new Set(projects.map((p) => p.id));
    const focusedLive = [...focusedIds].filter((id) => live.has(id));
    return focusedLive.length > 0 ? new Set(focusedLive) : new Set(filtered.map((p) => p.id));
  }, [projects, filtered, focusedIds]);
  const focusedProjects = useMemo(
    () => projects.filter((p) => focusedIds.has(p.id)),
    [projects, focusedIds]
  );

  // How to label the focus indicator in the legend slot:
  //  - "project": exactly one project focused → show its name (+ client)
  //  - "client":  all focused projects are the WHOLE of one client → show client dot + name
  //  - "multi":   anything else → show a count
  const focusInfo = useMemo(() => {
    if (focusedProjects.length === 0) return null;
    if (focusedProjects.length === 1) return { mode: "project", project: focusedProjects[0] };
    const clientKeys2 = new Set(focusedProjects.map((p) => (p.client || "").trim() || "__no_client__"));
    if (clientKeys2.size === 1) {
      const ck = [...clientKeys2][0];
      const allOfClient = filtered.filter((p) => ((p.client || "").trim() || "__no_client__") === ck);
      if (allOfClient.length === focusedProjects.length) return { mode: "client", clientKey: ck };
    }
    return { mode: "multi", count: focusedProjects.length };
  }, [focusedProjects, filtered]);

  const toggleProjectFocus = (id) => {
    setFocusedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Toggle every (currently visible) project of a client on/off together.
  const toggleClientFocus = (clientKey) => {
    const ids = (grouped[clientKey] || []).map((p) => p.id);
    if (ids.length === 0) return;
    setFocusedIds((prev) => {
      const next = new Set(prev);
      const allIn = ids.every((id) => next.has(id));
      ids.forEach((id) => (allIn ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  // Build buckets per chart range. Each bucket aggregates time per client.
  const { chartBuckets, rangeLabel, axisMaxRaw, totalSeconds } = useMemo(() => {
    let buckets = [];
    let label = "";
    if (chartRange === "week") {
      buckets = Array.from({ length: 7 }, (_, i) => {
        const date = addDays(weekStart, i);
        return { idx: i, date, label: `${WEEKDAYS[i]}, ${date.getDate()}. ${MONTHS_SHORT[date.getMonth()]}`, start: new Date(date), end: addDays(date, 1), perClient: {}, total: 0, isToday: date.getTime() === today.getTime() };
      });
      label = `KW ${getISOWeek(weekStart)}`;
    } else if (chartRange === "month") {
      const monthStart = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + monthOffset + 1, 1);
      // Weekly buckets covering the month — start at Monday on/before the 1st, cover until end
      const firstMon = getMonday(monthStart);
      let cur = new Date(firstMon);
      let i = 0;
      while (cur < monthEnd) {
        const end = addDays(cur, 7);
        // Clip boundary weeks to the month — otherwise the bridge week counted
        // neighbouring-month days into BOTH months' totals (and the month sum
        // disagreed with the year view).
        const bStart = cur < monthStart ? monthStart : new Date(cur);
        const bEnd = end > monthEnd ? monthEnd : end;
        buckets.push({ idx: i, date: cur, label: `KW ${getISOWeek(cur)}`, start: bStart, end: bEnd, perClient: {}, total: 0, isToday: today >= bStart && today < bEnd });
        cur = end;
        i++;
      }
      label = `${MONTHS_SHORT[monthStart.getMonth()]} ${monthStart.getFullYear()}`;
    } else {
      // year
      const year = today.getFullYear() + yearOffset;
      for (let m = 0; m < 12; m++) {
        const date = new Date(year, m, 1);
        const next = new Date(year, m + 1, 1);
        buckets.push({ idx: m, date, label: MONTHS_SHORT[m], start: date, end: next, perClient: {}, total: 0, isToday: today >= date && today < next });
      }
      label = String(year);
    }
    let total = 0;
    for (const e of entries) {
      if (!e.project_times || e.project_times.length === 0) continue;
      const day = new Date(e.started_at);
      const idx = buckets.findIndex((b) => day >= b.start && day < b.end);
      if (idx === -1) continue;
      for (const pt of e.project_times) {
        if (!pt.seconds) continue;
        if (!allowedProjectIds.has(pt.project_id)) continue;
        const client = (projectById[pt.project_id]?.client || "").trim() || "__no_client__";
        buckets[idx].perClient[client] = (buckets[idx].perClient[client] || 0) + pt.seconds;
        buckets[idx].total += pt.seconds;
        total += pt.seconds;
      }
    }
    const max = Math.max(0, ...buckets.map((b) => b.total));
    return { chartBuckets: buckets, rangeLabel: label, axisMaxRaw: max, totalSeconds: total };
  }, [chartRange, entries, weekStart, monthOffset, yearOffset, today, allowedProjectIds, projectById]);

  // Legend: unique clients appearing in the chart data
  const chartClients = useMemo(() => {
    const seen = new Set();
    chartBuckets.forEach((b) => Object.keys(b.perClient).forEach((c) => seen.add(c)));
    // Order the stacked segments + legend by colour (so same-coloured clients sit
    // together). Bars stack column-reverse (first = bottom); sorting descending puts
    // the "heaviest" colours (e.g. green) at the bottom. "Ohne Kunde" stays last.
    return [...seen].sort((a, b) => {
      if (a === "__no_client__") return 1;
      if (b === "__no_client__") return -1;
      const ca = colorForClient(a), cb = colorForClient(b);
      if (ca !== cb) return cb.localeCompare(ca);
      return a.localeCompare(b, "de");
    });
  }, [chartBuckets, colorForClient]);

  const axisMaxMs = useMemo(() => {
    const EIGHT_H = 8 * 3600;
    const targetBase = chartRange === "year" ? 40 * 4 * 3600 : chartRange === "month" ? 40 * 3600 : EIGHT_H;
    if (axisMaxRaw <= targetBase) return targetBase;
    return Math.ceil(axisMaxRaw / 3600) * 3600;
  }, [axisMaxRaw, chartRange]);
  const chartScaleMs = axisMaxMs / 0.88;
  const gridTicks = useMemo(() => {
    return Array.from({ length: 4 }, (_, i) => {
      const k = i + 1;
      const hours = (axisMaxMs * k) / 4 / 3600;
      const label = Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(0)}h`;
      return { k, pct: (k / 4) * 88, label };
    });
  }, [axisMaxMs]);


  if (!ready) {
    return (
      <div
        className="projects-page"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "var(--text-muted)" }}
      >
        Laden…
      </div>
    );
  }

  // PR-A: planning starts in 2024 and never runs into the future (no tracked time there).
  const navPeriodStart = chartRange === "week"
    ? weekStart
    : chartRange === "month"
    ? new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)
    : new Date(today.getFullYear() + yearOffset, 0, 1);
  const navCurOffset = chartRange === "week" ? weekOffset : chartRange === "month" ? monthOffset : yearOffset;
  const canNavForward = navCurOffset < 0;      // never past the present
  const canNavBack = navPeriodStart > PLAN_START; // never before 2024

  // "Alle" (= archived projects included) is the only non-default filter state.
  const hasActiveFilters = showArchived;

  return (
    <div className="projects-page">
      {err && <div className="wf-form-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="proj-toolbar">
        <div className="proj-range-toggle">
          {[
            { key: "week", label: "Woche" },
            { key: "month", label: "Monat" },
            { key: "year", label: "Jahr" },
          ].map((r) => (
            <button
              key={r.key}
              type="button"
              className={`proj-range-btn ${chartRange === r.key ? "is-active" : ""}`}
              onClick={() => setChartRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="proj-toolbar-right">
          <div className="proj-range-toggle" title="Archivierte Projekte ein-/ausblenden">
            {[
              { key: "alle", label: "Alle" },
              { key: "aktive", label: "Aktive" },
            ].map((o) => (
              <button
                key={o.key}
                type="button"
                className={`proj-range-btn ${((o.key === "alle") === showArchived) ? "is-active" : ""}`}
                onClick={() => setShowArchived(o.key === "alle")}
              >
                {o.label}
              </button>
            ))}
          </div>
          <button type="button" className="proj-add-btn" onClick={() => { setEditingClient(null); setEditingProject({ client: "", tabbed: true }); }} title="Projekt oder Kunde anlegen" aria-label="Projekt oder Kunde anlegen">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
        </div>
      </div>

      {/* Mobile-only floating filter button (bottom right) — holds the
          Alle/Aktive switch, which stays in the toolbar on the desktop.
          `.proj-filter-wrap` is display:none outside the 768px media query. */}
      <div className="filter-wrap proj-filter-wrap" ref={filterRef}>
        <button
          type="button"
          className={`btn-filter-toggle ${hasActiveFilters ? "has-filters" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
          title="Filter"
          aria-label="Filter"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {hasActiveFilters && <span className="filter-dot" />}
        </button>
        {showFilters && (
          <>
            <div className="mobile-backdrop" onClick={() => setShowFilters(false)} />
            <div className="filter-overlay">
              <div className="filter-bar">
                <div className="filter-header">
                  <span className="filter-title">Filter</span>
                  <button
                    type="button"
                    className="filter-reset"
                    style={{ visibility: hasActiveFilters ? "visible" : "hidden" }}
                    onClick={() => { setShowArchived(false); setShowFilters(false); }}
                  >
                    Zurücksetzen
                  </button>
                </div>
                <div className="filter-fields">
                  <div className="filter-item">
                    <label>Projekte</label>
                    <div className="filter-chips">
                      {[
                        { key: "alle", label: "Alle" },
                        { key: "aktive", label: "Aktive" },
                      ].map((o) => (
                        <button
                          key={o.key}
                          type="button"
                          className={`filter-chip ${((o.key === "alle") === showArchived) ? "filter-chip-active" : ""}`}
                          onClick={() => setShowArchived(o.key === "alle")}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Weekly chart */}
      <div className="wf-chart-card">
        <div className="wf-chart-total">
          <div className="wf-chart-nav">
            <button
              className="wf-nav-btn"
              disabled={!canNavBack}
              onClick={() => {
                if (!canNavBack) return;
                if (chartRange === "week") setWeekOffset((o) => o - 1);
                else if (chartRange === "month") setMonthOffset((o) => o - 1);
                else setYearOffset((o) => o - 1);
              }}
              title="Zurück"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <span className="wf-nav-today" onClick={() => { setWeekOffset(0); setMonthOffset(0); setYearOffset(0); }}>Heute</span>
            <RangeQuickPick
              range={chartRange}
              label={rangeLabel}
              today={today}
              curOffset={navCurOffset}
              onPick={(offset) => {
                if (chartRange === "week") setWeekOffset(offset);
                else if (chartRange === "month") setMonthOffset(offset);
                else setYearOffset(offset);
              }}
            />
            <button
              className="wf-nav-btn"
              disabled={!canNavForward}
              onClick={() => {
                if (!canNavForward) return;
                if (chartRange === "week") setWeekOffset((o) => o + 1);
                else if (chartRange === "month") setMonthOffset((o) => o + 1);
                else setYearOffset((o) => o + 1);
              }}
              title="Vor"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="wf-chart-legend wf-chart-legend-inline proj-chart-legend">
            {focusInfo ? (
              <span className="proj-focus-legend">
                {focusInfo.mode === "project" && (
                  <span className="proj-focus-legend-text" title={focusInfo.project.name}>
                    {focusInfo.project.name}
                    {focusInfo.project.client ? ` (${focusInfo.project.client})` : ""}
                  </span>
                )}
                {focusInfo.mode === "client" && (
                  <span className="wf-legend-item">
                    <span className="wf-legend-dot" style={{ background: focusInfo.clientKey === "__no_client__" ? "#6b7280" : colorForClient(focusInfo.clientKey) }} />
                    {focusInfo.clientKey === "__no_client__" ? "Ohne Kunde" : focusInfo.clientKey}
                  </span>
                )}
                {focusInfo.mode === "multi" && (
                  <span className="proj-focus-legend-text">{focusInfo.count} Projekte</span>
                )}
              </span>
            ) : (
              chartClients.map((c) => {
                const full = c === "__no_client__" ? "Ohne Kunde" : c;
                const short = full.length > 12 ? `${full.slice(0, 11)}…` : full;
                return (
                  <span key={c} className="wf-legend-item" title={full}>
                    <span className="wf-legend-dot" style={{ background: c === "__no_client__" ? "#6b7280" : colorForClient(c) }} />
                    {short}
                  </span>
                );
              })
            )}
          </div>
          <div className="wf-chart-total-main">
            <span className="wf-chart-total-label">Gesamt</span>
            <span className="wf-chart-total-value">
              {(() => {
                const total = Math.round((totalSeconds || 0) / 60);
                return (<>
                  {Math.floor(total / 60).toLocaleString("de-DE")}
                  <span className="wf-total-min">:{pad2(total % 60)}</span>
                </>);
              })()}
            </span>
          </div>
        </div>
        <div className="wf-chart">
          <div className="wf-chart-grid">
            {gridTicks.map((t) => (
              <div key={t.k} className="wf-chart-gridline" style={{ bottom: `${t.pct}%` }}>
                <span className="wf-chart-tick-label">{t.label}</span>
              </div>
            ))}
          </div>
          <div className="wf-chart-cols">
            {chartBuckets.map((d) => {
              const totalPct = chartScaleMs > 0 ? (d.total / chartScaleMs) * 100 : 0;
              // Stack segments in the same (alphabetical) order in every bar, not
              // in the order the entries happened to arrive.
              const clientKeys2 = chartClients.filter((c) => d.perClient[c]);
              return (
                <div key={d.idx} className="wf-chart-col">
                  <div className="wf-chart-bar-zone">
                    {d.total > 0 && <span className="wf-chart-val">{fmtDurationSec(d.total)}</span>}
                    <div className="wf-chart-bar" style={{ height: `${d.total > 0 ? Math.max(totalPct, 1.5) : 0}%` }}>
                      {clientKeys2.map((c) => (
                        <div
                          key={c}
                          className="wf-chart-seg"
                          style={{ height: `${(d.perClient[c] / d.total) * 100}%`, background: c === "__no_client__" ? "#6b7280" : colorForClient(c) }}
                        />
                      ))}
                    </div>
                  </div>
                  <span className={`wf-chart-day ${d.isToday ? "is-today" : ""}`}>{d.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="proj-card">
        {clientKeys.length === 0 && (
          <div className="wf-empty-week">
            {showArchived
              ? 'Noch keine Projekte. Oben rechts über „Neues Projekt“ anlegen.'
              : 'Keine aktiven Projekte. Über „Alle“ auch archivierte anzeigen.'}
          </div>
        )}
        {clientKeys.map((ck) => (
          <div key={ck} className="proj-group">
            <ProjectGroupHeader
              clientKey={ck}
              colorForClient={colorForClient}
              collapsed={collapsedClients.has(ck)}
              onToggle={() => toggleClientCollapse(ck)}
              onEdit={ck === "__no_client__" ? null : () => setEditingClient({ id: clientByName[ck]?.id, version: clientByName[ck]?.version, name: ck, color: colorForClient(ck), code: codeForClient(ck) })}
              onAddProject={() => setEditingProject({ client: ck === "__no_client__" ? "" : ck })}
              hasProjects={grouped[ck].length > 0}
              focusActive={grouped[ck].length > 0 && grouped[ck].every((p) => focusedIds.has(p.id))}
              onFocusToggle={() => toggleClientFocus(ck)}
            />
            {!collapsedClients.has(ck) && grouped[ck].length === 0 && (
              <div className="proj-group-empty">Noch keine Projekte — mit „+“ eins hinzufügen.</div>
            )}
            {!collapsedClients.has(ck) && grouped[ck].map((p) => {
              const trackedSec = p.tracked_seconds || 0;
              const estimatedH = p.estimated_hours || 0;
              const estimatedSec = estimatedH * 3600;
              const progress = estimatedSec > 0 ? Math.min(999, (trackedSec / estimatedSec) * 100) : 0;
              const personIds = p.person_ids || [];
              const showPersons = employees.length > 0 && personIds.length > 0 && personIds.length < employees.length;
              const personChips = showPersons ? personIds.map((id) => employees.find((e) => e.id === id)).filter(Boolean) : [];
              const rowClientColor = ck === "__no_client__" ? "#6b7280" : colorForClient(ck);
              return (
                <div
                  key={p.id}
                  className={`proj-row ${p.archived ? "proj-row-archived" : ""}`}
                  onClick={() => setEditingProject(p)}
                >
                  <span className="proj-abbr">{p.abbreviation || "—"}</span>
                  <div className="proj-name-wrap">
                    <span className="proj-name">{p.name}</span>
                  </div>
                  <div className="proj-persons">
                    {personChips.map((e) => (
                      <span key={e.id} className="proj-person-chip" title={e.name}>
                        {e.abbreviation || e.name.slice(0, 2)}
                      </span>
                    ))}
                  </div>
                  <div
                    className="proj-time proj-time-clickable"
                    title="Stunden-Übersicht nach Person anzeigen"
                    onClick={(ev) => { ev.stopPropagation(); setInfoProject(p); }}
                  >
                    <div className="proj-time-row">
                      <span className="proj-time-value">
                        {fmtDurationSec(trackedSec)}
                        {estimatedH > 0 && <><span className="proj-time-sep">/</span>{fmtDurationSec(estimatedSec)}</>}
                        <span className="proj-time-unit"> h</span>
                      </span>
                      {estimatedH > 0 && <span className="proj-time-percent">{progress.toFixed(2).replace(".", ",")} %</span>}
                    </div>
                    <div className="proj-progress" title={estimatedH > 0 ? `${progress.toFixed(2).replace(".", ",")} %` : "Keine Zielzeit"}>
                      <div
                        className={`proj-progress-bar ${progress > 100 ? "is-over" : ""} ${estimatedH === 0 ? "is-unset" : ""}`}
                        style={{ width: `${estimatedH > 0 ? Math.min(100, progress) : 0}%` }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`proj-focus-btn ${focusedIds.has(p.id) ? "is-active" : ""}`}
                    style={focusedIds.has(p.id) ? { background: rowClientColor, color: "#fff" } : undefined}
                    title={focusedIds.has(p.id) ? "Aus der Grafik entfernen" : "In der Grafik anzeigen"}
                    onClick={(ev) => { ev.stopPropagation(); toggleProjectFocus(p.id); }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {editingProject && (
        <ProjectModal
          project={editingProject}
          employees={activeEmployees}
          clients={allClients}
          projects={projects}
          codeForClient={codeForClient}
          tabBar={editingProject.tabbed ? (
            <AddTabs
              active="project"
              onProject={() => {}}
              onClient={() => { setEditingProject(null); setEditingClient({ name: "", color: defaultClientColor(""), code: "", isNew: true, tabbed: true }); }}
            />
          ) : null}
          onSave={(form) => handleSave(form, editingProject)}
          onClose={() => setEditingProject(null)}
          onDelete={editingProject.id ? () => { setConfirmDelete(editingProject); setEditingProject(null); } : null}
        />
      )}

      {editingClient && (
        <ClientModal
          name={editingClient.name}
          color={editingClient.color}
          code={editingClient.code || ""}
          isNew={!!editingClient.isNew}
          existingClients={allClients}
          tabBar={editingClient.tabbed ? (
            <AddTabs
              active="client"
              onProject={() => { setEditingClient(null); setEditingProject({ client: "", tabbed: true }); }}
              onClient={() => {}}
            />
          ) : null}
          onDelete={editingClient.isNew ? null : () => setConfirmDeleteClient({
            id: editingClient.id,
            name: editingClient.name,
            count: projects.filter((p) => (p.client || "") === editingClient.name).length,
          })}
          onClose={() => setEditingClient(null)}
          onSave={async (newName, newColor, newCode) => {
            try {
              if (editingClient.isNew) {
                await createClient({ name: newName, color: newColor, abbreviation: newCode });
              } else {
                // Backend re-points all of the client's projects on a rename.
                await updateClient(editingClient.id, { name: newName, color: newColor, abbreviation: newCode, version: editingClient.version });
              }
              await Promise.all([loadClients(), loadProjects()]);
            } catch (e) { setErr(e.message); }
            setEditingClient(null);
          }}
        />
      )}

      {infoProject && (
        <ProjectInfoModal
          project={infoProject}
          entries={entries}
          employees={employees}
          onClose={() => setInfoProject(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Projekt „${confirmDelete.name}“ wirklich löschen? Bestehende Zeiteinträge bleiben erhalten (ohne Projekt).`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {confirmDeleteClient && (
        <ConfirmDialog
          message={confirmDeleteClient.count > 0
            ? `Kunde „${confirmDeleteClient.name}“ wirklich löschen? Achtung: ${confirmDeleteClient.count} Projekt${confirmDeleteClient.count === 1 ? "" : "e"} werden mitgelöscht.`
            : `Kunde „${confirmDeleteClient.name}“ wirklich löschen?`}
          onConfirm={handleDeleteClient}
          onCancel={() => setConfirmDeleteClient(null)}
        />
      )}
    </div>
  );
}

/* Quick-select next to the range label — same idea as the calendar's chevron:
   instead of clicking the arrows repeatedly, jump straight to a week / month /
   year. The grid matches the active mode and honours the SAME bounds as the
   arrows (nothing before 2024, nothing in the future). */
function RangeQuickPick({ range, label, today, curOffset, onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const curYear = today.getFullYear();
  const todayMonday = getMonday(today);
  // Start of the period currently on screen — the popover opens on its year and
  // marks it as selected.
  const activeStart = range === "week"
    ? addDays(todayMonday, curOffset * 7)
    : range === "month"
    ? new Date(curYear, today.getMonth() + curOffset, 1)
    : new Date(curYear + curOffset, 0, 1);
  const activeYear = activeStart.getFullYear();
  const [pickYear, setPickYear] = useState(activeYear);

  useEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const r = triggerRef.current.getBoundingClientRect();
    const H = range === "week" ? 300 : 200;
    const W = 240;
    // Flip above the trigger when there is no room below it (phones) and keep the
    // popover inside the viewport horizontally.
    const flipUp = r.bottom + H + 8 > window.innerHeight && r.top > H + 8;
    setPos({
      top: flipUp ? Math.max(8, r.top - H - 4) : r.bottom + 6,
      left: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)),
    });
    setPickYear(activeYear);
  }, [open, range, activeYear]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // One cell description per mode: { text, offset, disabled, active }
  const cols = range === "week" ? 6 : 3;
  let cells;
  if (range === "week") {
    cells = Array.from({ length: lastWeekOfYear(pickYear) }, (_, i) => {
      const mon = getMondayOfWeek(pickYear, i + 1);
      return {
        key: i + 1,
        text: pad2(i + 1),
        title: `KW ${i + 1} (${pickYear})`,
        offset: Math.round((mon - todayMonday) / (7 * 86400000)),
        disabled: mon < PLAN_START || mon > todayMonday,
        active: mon.getTime() === activeStart.getTime(),
      };
    });
  } else if (range === "month") {
    cells = MONTHS_SHORT.map((m, i) => {
      const start = new Date(pickYear, i, 1);
      const offset = (pickYear - curYear) * 12 + (i - today.getMonth());
      return {
        key: i,
        text: m,
        title: `${m} ${pickYear}`,
        offset,
        disabled: start < PLAN_START || offset > 0,
        active: start.getTime() === activeStart.getTime(),
      };
    });
  } else {
    cells = Array.from({ length: Math.max(1, curYear - PLAN_START_YEAR + 1) }, (_, i) => {
      const y = PLAN_START_YEAR + i;
      return { key: y, text: String(y), title: String(y), offset: y - curYear, disabled: false, active: y === activeYear };
    });
  }
  const rows = [];
  for (let i = 0; i < cells.length; i += cols) rows.push(cells.slice(i, i + cols));

  const chevron = (points) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points={points} /></svg>
  );

  return (
    <>
      <span className="wf-week-label-wrap" ref={triggerRef}>
        <span className="wf-week-label">{label}</span>
        <button
          type="button"
          className="wf-nav-btn"
          onClick={() => setOpen((v) => !v)}
          title="Zeitraum wählen"
          aria-label="Zeitraum wählen"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </span>
      {open && pos && createPortal(
        <>
          {/* display:none on the desktop — on a phone it dims the page and closes on tap */}
          <div className="mobile-backdrop" onClick={() => setOpen(false)} />
          <div
            ref={popRef}
            className="week-picker-dropdown proj-pick-pop"
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 1100 }}
          >
            {/* The year grid needs no year nav — it already lists every year. */}
            {range !== "year" && (
              <div className="week-picker-header">
                <button type="button" className="week-picker-nav" disabled={pickYear <= PLAN_START_YEAR}
                  onClick={() => setPickYear((y) => y - 1)} title="Vorheriges Jahr">
                  {chevron("15 18 9 12 15 6")}
                </button>
                <span className="week-picker-year">{pickYear}</span>
                <button type="button" className="week-picker-nav" disabled={pickYear >= curYear}
                  onClick={() => setPickYear((y) => y + 1)} title="Nächstes Jahr">
                  {chevron("9 18 15 12 9 6")}
                </button>
              </div>
            )}
            <div className="week-picker-grid">
              {rows.map((row, ri) => (
                <div key={ri} className="week-picker-row">
                  {row.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`week-picker-cell ${range === "week" ? "" : "proj-pick-cell-wide"} ${c.active ? "week-picker-cell-selected" : ""}`}
                      disabled={c.disabled}
                      title={c.title}
                      onClick={() => { onPick(c.offset); setOpen(false); }}
                    >
                      {c.text}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

function ProjectInfoModal({ project, entries, employees, onClose }) {
  // Sum tracked seconds on this project per user across all time entries.
  const rows = useMemo(() => {
    const perUser = new Map();
    let total = 0;
    for (const e of entries) {
      if (!e.project_times) continue;
      for (const pt of e.project_times) {
        if (pt.project_id !== project.id || !pt.seconds) continue;
        perUser.set(e.user_id, (perUser.get(e.user_id) || 0) + pt.seconds);
        total += pt.seconds;
      }
    }
    // Work from before time tracking has no person attached, but it belongs in the
    // total — otherwise the breakdown wouldn't add up to the project's hours.
    const external = project.external_seconds || 0;
    if (external > 0) {
      perUser.set("external", external);
      total += external;
    }
    const list = [...perUser.entries()].map(([userId, seconds]) => {
      const emp = userId === "external" ? null : employees.find((m) => m.id === userId);
      return {
        userId,
        name: userId === "external" ? "Vor Zeiterfassung" : (emp?.name || `#${userId}`),
        seconds,
        pct: total > 0 ? (seconds / total) * 100 : 0,
      };
    });
    list.sort((a, b) => b.seconds - a.seconds);
    return { list, total };
  }, [entries, employees, project.id, project.external_seconds]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Stunden Übersicht</h2>
          <button className="modal-close-btn" onClick={onClose} title="Schließen">&times;</button>
        </div>

        {rows.list.length === 0 ? (
          <div className="wf-empty-week">Noch keine getrackte Zeit auf diesem Projekt.</div>
        ) : (
          <div className="proj-info-list">
            {rows.list.map((r) => (
              <div key={r.userId} className="proj-info-row">
                <span className="proj-info-person">{r.name}</span>
                <span className="proj-info-hours">{fmtDurationSec(r.seconds)} h</span>
                <span className="proj-info-pct">{Math.round(r.pct)} %</span>
              </div>
            ))}
            <div className="proj-info-row proj-info-total">
              <span className="proj-info-person">Gesamt</span>
              <span className="proj-info-hours">{fmtDurationSec(rows.total)} h</span>
              <span className="proj-info-pct" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectGroupHeader({ clientKey, colorForClient, collapsed, onToggle, onEdit, onAddProject, hasProjects, focusActive, onFocusToggle }) {
  const isNoClient = clientKey === "__no_client__";
  const label = isNoClient ? "Ohne Kunde" : clientKey;
  const color = isNoClient ? null : colorForClient(clientKey);
  return (
    <div className="proj-group-header" style={color ? { "--client-color": color } : {}}>
      <button type="button" className="proj-group-toggle" onClick={onToggle} title={collapsed ? "Ausklappen" : "Einklappen"}>
        <svg className={`proj-group-chevron ${collapsed ? "collapsed" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {onEdit ? (
        <button type="button" className="proj-group-name proj-group-name-btn" onClick={onEdit} title="Kunde bearbeiten">{label}</button>
      ) : (
        <span className="proj-group-name">{label}</span>
      )}
      <button type="button" className="proj-group-add" onClick={onAddProject} title="Projekt zu diesem Kunden hinzufügen">+</button>
      {hasProjects && (
        <button
          type="button"
          className={`proj-group-focus ${focusActive ? "is-active" : ""}`}
          onClick={onFocusToggle}
          title={focusActive ? "Kunde aus der Grafik entfernen" : "Alle Projekte dieses Kunden in der Grafik anzeigen"}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
        </button>
      )}
    </div>
  );
}

// Tab switcher shown at the top of the add-new modal (like the appointment form).
function AddTabs({ active, onProject, onClient }) {
  return (
    <div className="proj-add-tabs">
      <button type="button" className={`proj-add-tab ${active === "project" ? "is-active" : ""}`} onClick={onProject}>Projekt</button>
      <button type="button" className={`proj-add-tab ${active === "client" ? "is-active" : ""}`} onClick={onClient}>Kunde</button>
    </div>
  );
}

function ClientModal({ name, color, code = "", isNew = false, existingClients, onClose, onSave, onDelete, tabBar = null }) {
  const [editName, setEditName] = useState(name);
  const [editColor, setEditColor] = useState(color);
  const [editCode, setEditCode] = useState(code);
  // Track whether the Kürzel was set manually — otherwise auto-derive it from the name.
  const [codeTouched, setCodeTouched] = useState(!!code);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState(null);
  const [err, setErr] = useState("");
  const colorBtnRef = useRef(null);
  const colorPickerRef = useRef(null);

  useEffect(() => {
    if (!showPicker) return;
    const onDoc = (e) => {
      if (colorBtnRef.current && !colorBtnRef.current.contains(e.target) &&
          colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showPicker]);

  const onNameChange = (value) => {
    setEditName(value);
    if (!codeTouched) setEditCode(autoClientCode(value));
  };

  const submit = async () => {
    const newName = editName.trim();
    if (!newName) { setErr("Bitte einen Namen angeben."); return; }
    if (newName !== name && existingClients.includes(newName)) {
      setErr("Ein Kunde mit diesem Namen existiert bereits.");
      return;
    }
    await onSave(newName, editColor, editCode.trim());
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${tabBar ? "proj-add-tabbed" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{tabBar ? "Neu anlegen" : (isNew ? "Neuer Kunde" : "Kunde bearbeiten")}</h2>
          <button className="modal-close-btn" onClick={onClose} title="Schließen">&times;</button>
        </div>
        {tabBar}

        {err && <div className="form-error">{err}</div>}

        <div className="form-row">
          <label className="task-form-label">Name</label>
          <input
            type="text"
            value={editName}
            onChange={(e) => onNameChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="Kundenname"
            autoFocus
          />
        </div>

        <div className="proj-client-row">
          <div className="form-row proj-client-color">
            <label className="task-form-label">Farbe</label>
            <div className="area-color-picker-wrap">
              <button
                ref={colorBtnRef}
                type="button"
                className="area-color-btn"
                style={{ background: editColor }}
                onClick={() => {
                  if (!showPicker && colorBtnRef.current) {
                    const rect = colorBtnRef.current.getBoundingClientRect();
                    setPickerPos({ top: rect.top, left: rect.right + 8 });
                  }
                  setShowPicker(!showPicker);
                }}
              />
              {showPicker && (
                <div className="area-color-popover" ref={colorPickerRef} style={pickerPos ? { top: pickerPos.top, left: pickerPos.left } : {}}>
                  <HexColorPicker color={editColor} onChange={setEditColor} />
                  <input
                    className="area-color-hex-input"
                    value={editColor}
                    onChange={(e) => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setEditColor(e.target.value); }}
                    maxLength={7}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="form-row proj-client-code">
            <label className="task-form-label">Kürzel</label>
            <input
              type="text"
              value={editCode}
              onChange={(e) => { setCodeTouched(true); setEditCode(e.target.value.toUpperCase()); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder="ABC"
              maxLength={4}
            />
          </div>
        </div>

        <div className="form-actions">
          {!isNew && onDelete && (
            <button type="button" className="btn btn-danger" onClick={onDelete}>Löschen</button>
          )}
          <div className="form-actions-right">
            <button className="btn btn-primary" onClick={submit}>Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Suggested project abbreviation: the client's Kürzel followed by the next running
// number = highest number already used for that client + 1 (so deleting a project
// in the middle leaves a gap but never causes a collision). e.g. with MÜL001 and
// MÜL003 present → "MÜL004". Empty when no client/code yet.
function suggestAbbreviation(client, code, projects, currentId) {
  if (!code) return "";
  const key = (client || "").trim().toLowerCase();
  let maxNum = 0;
  for (const p of projects) {
    if (p.id === currentId) continue;
    if ((p.client || "").trim().toLowerCase() !== key) continue;
    const m = /(\d+)\s*$/.exec(p.abbreviation || "");
    if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
  }
  return `${code}${String(maxNum + 1).padStart(3, "0")}`;
}

function ProjectModal({ project, employees, clients = [], projects = [], codeForClient = () => "", onSave, onClose, onDelete, tabBar = null }) {
  const isNew = !project?.id;
  const initialClient = project?.client || "";
  const [form, setForm] = useState({
    name: project?.name || "",
    // Prefill the suggested abbreviation when a client is already set (e.g. opened
    // via a client's "+" button) and none was stored yet.
    abbreviation: project?.abbreviation || (initialClient ? suggestAbbreviation(initialClient, codeForClient(initialClient), projects, project?.id) : ""),
    client: initialClient,
    estimated_hours: project?.estimated_hours || 0,
    person_ids: isNew ? employees.map((e) => e.id) : (project?.person_ids || []),
    archived: project?.archived || false,
  });
  // Auto-fill the abbreviation from the client until the user edits it manually.
  const [abbrTouched, setAbbrTouched] = useState(!!project?.abbreviation);
  const [err, setErr] = useState("");

  const setClient = (value) => {
    setForm((f) => ({
      ...f,
      client: value,
      abbreviation: abbrTouched ? f.abbreviation : suggestAbbreviation(value, codeForClient(value), projects, project?.id),
    }));
  };

  const clientQuery = (form.client || "").trim().toLowerCase();

  // Warn (but don't block) when the Kürzel is already used by another project of
  // the same client.
  const abbrNorm = (form.abbreviation || "").trim().toLowerCase();
  const abbrDuplicate = !!abbrNorm && projects.some(
    (p) => p.id !== project?.id
      && (p.client || "").trim().toLowerCase() === clientQuery
      && (p.abbreviation || "").trim().toLowerCase() === abbrNorm
  );

  const togglePerson = (id) => {
    const ids = form.person_ids;
    setForm({ ...form, person_ids: ids.includes(id) ? ids.filter((p) => p !== id) : [...ids, id] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("Bitte einen Namen angeben."); return; }
    try {
      await onSave({
        name: form.name.trim(),
        abbreviation: (form.abbreviation || "").trim(),
        client: (form.client || "").trim(),
        estimated_hours: parseFloat(form.estimated_hours) || 0,
        person_ids: form.person_ids,
        archived: form.archived,
      });
      onClose();
    } catch (e) { setErr(e.message); }
  };

  const toggleArchive = async () => {
    try {
      await onSave({
        name: form.name.trim() || (project?.name || ""),
        abbreviation: (form.abbreviation || "").trim(),
        client: (form.client || "").trim(),
        estimated_hours: parseFloat(form.estimated_hours) || 0,
        person_ids: form.person_ids,
        archived: !form.archived,
      });
      onClose();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${tabBar ? "proj-add-tabbed" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{tabBar ? "Neu anlegen" : (isNew ? "Neues Projekt" : "Projekt bearbeiten")}</h2>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>
        {tabBar}

        <form className="proj-form" onSubmit={submit}>
          {/* Kunde comes before the Kürzel on purpose: picking the client is what
              feeds suggestAbbreviation. */}
          <div className="proj-form-grid">
            <div className="proj-form-field proj-form-field-wide">
              <label className="task-form-label">Kunde</label>
              <CustomSelect
                value={clients.some((c) => c === form.client) ? form.client : ""}
                placeholder="Ohne Kunde"
                options={[
                  { value: "", label: "Ohne Kunde" },
                  ...clients.map((c) => ({ value: c, label: c })),
                ]}
                onChange={(v) => setClient(v)}
              />
            </div>
            <div className="proj-form-field proj-form-field-abbr">
              <label className="task-form-label">Kürzel</label>
              <input className={`wf-input ${abbrDuplicate ? "wf-input-warn" : ""}`} maxLength={10} value={form.abbreviation}
                placeholder="ABC001"
                onChange={(e) => { setAbbrTouched(true); setForm({ ...form, abbreviation: e.target.value.toUpperCase() }); }} />
            </div>
          </div>
          {abbrDuplicate && (
            <div className="proj-abbr-warn">⚠ Dieses Kürzel ist bei diesem Kunden bereits vergeben. Speichern ist trotzdem möglich.</div>
          )}

          <div className="proj-form-grid">
            <div className="proj-form-field proj-form-field-wide">
              <label className="task-form-label">Name</label>
              <input className="wf-input" value={form.name} autoFocus maxLength={200}
                placeholder="Projektname"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="proj-form-field">
              <label className="task-form-label">Geplante Stunden</label>
              <input className="wf-input wf-input-no-spin" type="number" inputMode="decimal" min="0" step="0.5" value={form.estimated_hours}
                placeholder="40"
                onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} />
            </div>
          </div>

          <div className="proj-form-field proj-form-field-persons">
            <label className="task-form-label">Beteiligte Personen</label>
            <div className="task-person-chips">
              {employees.map((m) => {
                const sel = form.person_ids.includes(m.id);
                return (
                  <button key={m.id} type="button"
                    className={`task-person-chip ${sel ? "task-person-chip-active" : ""}`}
                    style={sel ? { background: m.color, borderColor: m.color } : {}}
                    onClick={() => togglePerson(m.id)}>
                    {m.abbreviation || m.name}
                  </button>
                );
              })}
            </div>
          </div>

          {err && <div className="wf-form-error">{err}</div>}

          <div className="proj-form-actions">
            <div className="proj-actions-left">
              {!isNew && onDelete && (
                <button type="button" className="btn btn-danger" onClick={onDelete}>Löschen</button>
              )}
            </div>
            <div className="proj-actions-center">
              {!isNew && (
                <button type="button" className="btn btn-secondary" onClick={toggleArchive}>
                  {form.archived ? "Reaktivieren" : "Archivieren"}
                </button>
              )}
            </div>
            <div className="proj-actions-right">
              <button type="submit" className="btn btn-primary">{isNew ? "Erstellen" : "Speichern"}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
